import { useEffect, useRef, useState } from "react";
import { CheckCircle2, Clock, MessageCircle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import DailyProgramCard, { nextWorkSentence } from "./DailyProgramCard";

const actionCopy = {
  request_under_review: [
    "Talebiniz inceleniyor",
    "Seçtiğiniz program için talebiniz alındı. Onaylandığında başlangıç anketiniz açılacak.",
  ],
  request_reviewed: [
    "Talebiniz değerlendirildi",
    "Program erişiminiz hazırlanıyor. Durum değiştiğinde paneliniz otomatik olarak güncellenecek.",
  ],
  complete_questionnaire: [
    "Başlangıç sorularını yanıtla",
    "Yanıtların kişisel çalışmanı hazırlamak için kullanılacak.",
  ],
  plan_preparing: [
    "İlk çalışman hazırlanıyor",
    "Anket değerlendirmesi tamamlandığında bugünün çalışması burada açılacak.",
  ],
  guide_plan_preparing: [
    "Çalışman rehber tarafından hazırlanıyor",
    "Anket yanıtların Şeref Tolga Kuralay tarafından değerlendiriliyor.",
  ],
  guide_review: [
    "Rehber onayı bekleniyor",
    "Hazırlanan çalışma onaylandıktan sonra panelinde görünecek.",
  ],
  follow_plan: ["Çalışma programın aktif", "Bugün uygulayacağın çalışma aşağıda yer alıyor."],
};

const APPROVAL_WHATSAPP_URL = `https://wa.me/905327991130?text=${encodeURIComponent(
  "Merhaba, başlangıç anketimi tamamladım. Kişisel çalışma planım rehber onayında görünüyor. Program başlangıcı ve çalışma onayı için görüşmek istiyorum.",
)}`;

const dailyProgramCopy = {
  awaiting_guide_plan: [
    "Yeni çalışma hazırlanıyor",
    "Rehberiniz sonraki çalışmanızı hazırladığında bu alanda açılacak.",
  ],
  waiting_next_day: [
    "Sıradaki çalışma bekleniyor",
    "Sıradaki çalışmanız zamanı geldiğinde otomatik açılacak.",
  ],
  resting: [
    "7 günlük dinlenme sürecine girdiniz",
    "Dinlenme süreniz devam ediyor.",
  ],
  locked: ["Deneyim kaydı gerekiyor", "Yeni çalışmanın açılması için deneyim kaydını tamamla."],
  completed: ["Programın tamamlandı", "Program ekranın kapandı; tamamlama bilgin kaydedildi."],
  frozen: [
    "Programın geçici olarak durduruldu",
    "Program yeniden açıldığında aynı çalışma üzerinden devam edeceksin.",
  ],
};

const machineKey = (prefix) =>
  `${prefix}:${globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`}`;

const processActions = new Set(["request_under_review", "request_reviewed"]);

const workActions = new Set([
  "complete_questionnaire",
  "plan_preparing",
  "guide_plan_preparing",
  "guide_review",
  "follow_plan",
]);

export default function ClientAutomationPanel({
  automation,
  refresh,
  surface = "process",
  currentWorkAvailable = false,
  onOpenGuide,
  onExperienceJournalOpen,
}) {
  const [schema, setSchema] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loadingSchema, setLoadingSchema] = useState(false);
  const [saving, setSaving] = useState(false);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [finishingQuestionnaire, setFinishingQuestionnaire] = useState(false);
  const [error, setError] = useState("");
  const [clockNow, setClockNow] = useState(() => Date.now());
  const serverClockRef = useRef({ server: null, client: Date.now() });
  const nextAction = automation?.next_action;
  const preparation = automation?.preparation;
  const isProcessSurface = surface === "process";
  const isWorkSurface = surface === "work";
  const surfaceAllowsAction =
    (isProcessSurface && processActions.has(nextAction)) ||
    (isWorkSurface && workActions.has(nextAction));
  const programStatus = automation?.daily_program?.status;
  const programCopy = automation?.daily_program?.mantra_approval_pending
    ? ["Mantra çalışmanız rehber onayında", "Yeni çalışmanız rehberiniz onayladıktan sonra açılacak."]
    : programStatus === "waiting_next_day"
    ? [dailyProgramCopy.waiting_next_day[0], nextWorkSentence(automation?.daily_program?.next_transition_at)]
    : dailyProgramCopy[programStatus];
  const copy = (nextAction === "follow_plan" && programCopy) ||
    actionCopy[nextAction] || ["Program yolculuğu", "Durumunuz otomatik olarak güncellenecek."];
  const offer = automation?.offer;
  const isSpecialInvitationLink = automation?.invitation_kind === "special_link";
  const panelTablePlanReady =
    automation?.plan?.delivery_format === "panel_table" &&
    (automation?.plan?.blocks || []).length > 0;
  const dailyProgram = automation?.daily_program;
  const activatedProgramStillMaterializing =
    nextAction === "follow_plan" &&
    !panelTablePlanReady &&
    (!dailyProgram ||
      dailyProgram.status === "materializing" ||
      (dailyProgram.status === "active" && !dailyProgram.current_step?.practice));
  const shouldLoadQuestionnaire =
    isWorkSurface && nextAction === "complete_questionnaire" && offer?.id;

  useEffect(() => {
    if (!shouldLoadQuestionnaire) return undefined;
    let active = true;
    setSchema(null);
    setAnswers({});
    setQuestionIndex(0);
    setFinishingQuestionnaire(false);
    setError("");
    setLoadingSchema(true);
    api
      .get("/client-portal/questionnaire", { params: { offer_id: offer.id } })
      .then(({ data }) => {
        if (active) setSchema(data);
      })
      .catch((requestError) => {
        if (active) setError(formatApiError(requestError));
      })
      .finally(() => {
        if (active) setLoadingSchema(false);
      });
    return () => {
      active = false;
    };
  }, [offer?.id, shouldLoadQuestionnaire]);

  useEffect(() => {
    if (
      !isWorkSurface ||
      (!["plan_preparing", "guide_plan_preparing", "guide_review"].includes(nextAction) &&
        !activatedProgramStillMaterializing)
    )
      return undefined;
    let active = true;
    const refreshIntervalId =
      typeof refresh === "function"
        ? globalThis.setInterval(() => {
            if (active) Promise.resolve(refresh()).catch(() => {});
          }, 2500)
        : null;
    return () => {
      active = false;
      if (refreshIntervalId) globalThis.clearInterval(refreshIntervalId);
    };
  }, [activatedProgramStillMaterializing, isWorkSurface, nextAction, refresh]);

  useEffect(() => {
    if (!isWorkSurface || preparation?.state !== "scheduled") return undefined;
    serverClockRef.current = {
      server: preparation?.server_now ? new Date(preparation.server_now).getTime() : Date.now(),
      client: Date.now(),
    };
    setClockNow(Date.now());
    const timer = globalThis.setInterval(() => setClockNow(Date.now()), 1000);
    return () => globalThis.clearInterval(timer);
  }, [isWorkSurface, preparation?.server_now, preparation?.state]);

  useEffect(() => {
    if (
      typeof refresh !== "function" ||
      !surfaceAllowsAction ||
      !nextAction ||
      [
        "select_package",
        "automation_unavailable",
        "complete_questionnaire",
        "plan_preparing",
        "guide_plan_preparing",
        "guide_review",
        "follow_plan",
      ].includes(nextAction)
    )
      return undefined;
    let active = true;
    const poll = () => {
      if (active && globalThis.document?.visibilityState === "visible") {
        Promise.resolve(refresh()).catch(() => {});
      }
    };
    const intervalId = globalThis.setInterval(poll, 3000);
    globalThis.addEventListener?.("focus", poll);
    return () => {
      active = false;
      globalThis.clearInterval(intervalId);
      globalThis.removeEventListener?.("focus", poll);
    };
  }, [nextAction, refresh, surfaceAllowsAction]);

  const questions = schema?.questions || [];
  const currentQuestion = questions[questionIndex];
  const currentQuestionText = currentQuestion?.label || currentQuestion?.title || "";
  const questionPromptLengthClass =
    currentQuestionText.length > 105
      ? "stitch-question-prompt--long"
      : "stitch-question-prompt--standard";
  const currentAnswer = currentQuestion ? answers[currentQuestion.id] : undefined;
  const currentAnswerComplete = Boolean(currentQuestion) && (
    currentQuestion.type === "multi_choice"
      ? Array.isArray(currentAnswer) && currentAnswer.length > 0
      : currentQuestion.type === "short_text"
        ? Boolean(String(currentAnswer || "").trim())
        : currentAnswer !== undefined && currentAnswer !== null && currentAnswer !== ""
  );

  const submitAnswers = async (completedAnswers) => {
    if (!offer?.id || !schema?.version || saving || finishingQuestionnaire) return;
    setFinishingQuestionnaire(true);
    setError("");
    await new Promise((resolve) => globalThis.setTimeout(resolve, 3000));
    setSaving(true);
    try {
      await api.post(
        "/client-portal/questionnaire",
        {
          offer_id: offer.id,
          schema_version: schema.version,
          answers: completedAnswers,
        },
        { headers: { "Idempotency-Key": machineKey("questionnaire") } },
      );
      toast.success("Başlangıç anketiniz kaydedildi; plan hazırlığı başladı.");
      await refresh();
    } catch (requestError) {
      setError(formatApiError(requestError));
      setFinishingQuestionnaire(false);
    } finally {
      setSaving(false);
    }
  };

  const setChoice = (question, option) => {
    setAnswers((current) => ({ ...current, [question.id]: option }));
  };

  const setTextAnswer = (question, value) => {
    setAnswers((current) => ({ ...current, [question.id]: value }));
  };

  const toggleMulti = (question, option) => {
    setAnswers((current) => {
      const selected = Array.isArray(current[question.id]) ? current[question.id] : [];
      const exists = selected.some((item) => Object.is(item, option));
      return {
        ...current,
        [question.id]: exists
          ? selected.filter((item) => !Object.is(item, option))
          : [...selected, option],
      };
    });
  };

  if (!automation) return null;
  const tablePlanAvailable =
    nextAction === "follow_plan" &&
    automation?.plan?.delivery_format === "panel_table" &&
    (automation?.plan?.blocks || []).length > 0;
  const dailyProgramAvailable = Boolean(
    automation?.daily_program && automation.daily_program.status !== "materializing",
  );
  const usesImmersiveStitchScreen =
    shouldLoadQuestionnaire ||
    ["plan_preparing", "guide_plan_preparing", "guide_review"].includes(nextAction) ||
    dailyProgramAvailable;
  if (
    (!isProcessSurface && !isWorkSurface) ||
    !surfaceAllowsAction ||
    (isWorkSurface &&
      nextAction === "follow_plan" &&
      currentWorkAvailable &&
      !tablePlanAvailable &&
      !dailyProgramAvailable)
  )
    return null;

  const packageMonths = offer?.package_months || automation?.request?.package_months;
  return (
    <>
      <section
      className={`stitch-automation-panel${
        usesImmersiveStitchScreen ? " stitch-automation-panel--immersive" : ""
      }`}
      aria-labelledby="automation-journey-title"
    >
      <div className={usesImmersiveStitchScreen ? "sr-only" : "stitch-automation-heading"}>
        <div>
          <div className="stitch-automation-eyebrow">
            {isProcessSurface ? "Program erişimi" : "Çalışma hazırlığı"}
          </div>
          <h2
            id="automation-journey-title"
            role="status"
            aria-live="polite"
            aria-atomic="true"
            className="stitch-automation-title"
          >
            {copy[0]}
          </h2>
          <p className="stitch-automation-copy">{copy[1]}</p>
          {isProcessSurface && packageMonths && (
            <p className="text-[11px] text-pleiades-muted mt-3">
              {packageMonths === 12 ? "1 yıllık" : `${packageMonths} aylık`} kişisel program
            </p>
          )}
        </div>
        <StatusPill action={nextAction} />
      </div>

      {error && (
        <div
          role="alert"
          className="mx-5 sm:mx-7 mt-5 p-3 border border-red-200 bg-red-50 text-red-700 text-[12px]"
        >
          {error}
        </div>
      )}

      {shouldLoadQuestionnaire && (
        <div className="stitch-questionnaire">
          {loadingSchema && (
            <div role="status" className="stitch-questionnaire-loading">
              Başlangıç anketi yükleniyor…
            </div>
          )}
          {finishingQuestionnaire ? (
            <div className="stitch-questionnaire-finishing" role="status" aria-live="polite">
              <div className="stitch-questionnaire-finishing__marker" aria-hidden="true">
                <CheckCircle2 />
              </div>
              <div className="stitch-questionnaire-finishing__eyebrow">Anket tamamlandı</div>
              <strong>Yanıtlarınız alındı</strong>
              <p>İlk çalışmanız Şeref Tolga Kuralay tarafından hazırlanıyor.</p>
              <div className="stitch-questionnaire-finishing__line" aria-hidden="true">
                <span />
              </div>
              <span className="stitch-questionnaire-finishing__status">Lütfen bekleyin…</span>
            </div>
          ) : currentQuestion ? (
            <>
              <div className="stitch-questionnaire-progress">
                <span>
                  Soru {questionIndex + 1} / {questions.length}
                </span>
                <div
                  role="progressbar"
                  aria-label="Başlangıç anketi ilerlemesi"
                  aria-valuemin={0}
                  aria-valuemax={questions.length}
                  aria-valuenow={questionIndex + 1}
                >
                  <span style={{ width: `${((questionIndex + 1) / questions.length) * 100}%` }} />
                </div>
              </div>
              <fieldset className="stitch-question">
                <legend className={`stitch-question-prompt ${questionPromptLengthClass}`}>
                  {currentQuestionText}
                </legend>
                {currentQuestion.description && (
                  <p className="stitch-question-description">
                    {currentQuestion.description}
                  </p>
                )}
                {currentQuestion.type === "short_text" ? (
                  <textarea
                    value={answers[currentQuestion.id] || ""}
                    onChange={(event) => setTextAnswer(currentQuestion, event.target.value)}
                    maxLength={500}
                    rows={5}
                    required
                    className="stitch-question-textarea"
                  />
                ) : (
                  <div className="stitch-question-options">
                    {(currentQuestion.options || []).map((option, optionIndex) => {
                      const checked =
                        currentQuestion.type === "multi_choice"
                          ? (answers[currentQuestion.id] || []).some((item) =>
                              Object.is(item, option),
                            )
                          : Object.is(answers[currentQuestion.id], option);
                      return (
                        <label
                          key={`${currentQuestion.id}-${optionIndex}`}
                          className="stitch-question-option"
                        >
                          <input
                            type={currentQuestion.type === "multi_choice" ? "checkbox" : "radio"}
                            name={currentQuestion.id}
                            checked={checked}
                            onChange={() =>
                              currentQuestion.type === "multi_choice"
                                ? toggleMulti(currentQuestion, option)
                                : setChoice(currentQuestion, option)
                            }
                          />
                          <span>
                            {currentQuestion.option_labels?.[String(option)]
                              ? `${option} — ${currentQuestion.option_labels[String(option)]}`
                              : String(option)}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </fieldset>
              <nav className="pleiades-questionnaire-nav" aria-label="Anket gezinme düğmeleri">
                <button
                  type="button"
                  className="pleiades-questionnaire-nav-button pleiades-questionnaire-nav-button--back"
                  disabled={questionIndex === 0 || saving}
                  onClick={() => setQuestionIndex((current) => Math.max(0, current - 1))}
                >
                  Geri
                </button>
                <button
                  type="button"
                  className="pleiades-questionnaire-nav-button pleiades-questionnaire-nav-button--next"
                  disabled={saving}
                  aria-disabled={!currentAnswerComplete || saving}
                  onClick={() => {
                    if (!currentAnswerComplete || saving) return;
                    if (questionIndex < questions.length - 1) {
                      setQuestionIndex((current) => current + 1);
                    } else {
                      submitAnswers(answers);
                    }
                  }}
                >
                  {questionIndex < questions.length - 1 ? "Devam et" : "Anketi tamamla"}
                </button>
              </nav>
            </>
          ) : null}
        </div>
      )}

      {!dailyProgramAvailable && ["plan_preparing", "guide_plan_preparing", "guide_review"].includes(nextAction) && (
        <div className="stitch-preparation">
          <div className={`stitch-preparation-card${nextAction !== "plan_preparing" ? " stitch-preparation-card--guide-review" : ""}`}>
            {(() => {
              const effectiveNow =
                serverClockRef.current.server === null
                  ? clockNow
                  : serverClockRef.current.server + (clockNow - serverClockRef.current.client);
              const readyAt = preparation?.ready_at
                ? new Date(preparation.ready_at).getTime()
                : null;
              const remainingSeconds = readyAt
                ? Math.max(0, Math.ceil((readyAt - effectiveNow) / 1000))
                : 0;
              const totalSeconds = Math.max(60, Number(preparation?.delay_minutes || 1) * 60);
              const elapsedSeconds = Math.min(
                totalSeconds,
                Math.max(0, totalSeconds - remainingSeconds),
              );
              const minutes = Math.floor(remainingSeconds / 60);
              const seconds = String(remainingSeconds % 60).padStart(2, "0");
              const scheduled =
                nextAction === "plan_preparing" &&
                preparation?.state === "scheduled" &&
                remainingSeconds > 0;
              return (
                <>
                  <div className="stitch-preparation-intro">
                    <CheckCircle2 aria-hidden="true" />
                    <div className="stitch-preparation-title">
                      {nextAction === "guide_review"
                        ? "Rehber onayı bekleniyor"
                        : nextAction === "guide_plan_preparing"
                          ? "Çalışman rehber tarafından hazırlanıyor"
                          : "İlk çalışman hazırlanıyor"}
                    </div>
                    <div className="stitch-preparation-copy">
                      {nextAction === "guide_review"
                        ? "Hazırlanan çalışmanız Şeref Tolga Kuralay'ın onayından sonra panelinizde açılacaktır."
                        : nextAction === "guide_plan_preparing"
                          ? "Anket yanıtlarınız Şeref Tolga Kuralay tarafından değerlendiriliyor. Çalışmanız hazırlandığında onay aşamasına geçecektir."
                          : scheduled
                          ? "Planlanan hazırlık süresinin bitmesine"
                          : "İlk çalışmanız hazırlanıyor. Bu sayfayı açık tutmanız gerekmez."}
                    </div>
                    {scheduled && (
                      <div
                        role="timer"
                        aria-live="off"
                        className="stitch-preparation-timer"
                      >
                        {minutes}:{seconds} kaldı
                      </div>
                    )}
                  </div>
                  {["guide_plan_preparing", "guide_review"].includes(nextAction) && (
                    <>
                      <ol className="stitch-preparation-steps">
                        <li data-state="done">Davet kodu doğrulandı</li>
                        <li data-state="done">Başlangıç anketi tamamlandı</li>
                        <li data-state="current">
                          {nextAction === "guide_review"
                            ? "Çalışma onayı bekleniyor"
                            : "Kişisel çalışma hazırlanıyor"}
                        </li>
                      </ol>
                      {nextAction === "guide_review" && !isSpecialInvitationLink && (
                        <a
                          href={APPROVAL_WHATSAPP_URL}
                          target="_blank"
                          rel="noreferrer"
                          className="stitch-primary-button mt-4"
                        >
                          <MessageCircle size={17} aria-hidden="true" />
                          Çalışma onayı için WhatsApp’tan görüş
                        </a>
                      )}
                    </>
                  )}
                  <div
                    role="progressbar"
                    aria-label="Çalışma tablosu hazırlama ilerlemesi"
                    aria-valuemin={0}
                    aria-valuemax={totalSeconds}
                    aria-valuenow={scheduled ? elapsedSeconds : undefined}
                    className="stitch-preparation-progress"
                  >
                    <div
                      className={scheduled ? "" : "w-1/2 animate-pulse motion-reduce:animate-none"}
                      style={
                        scheduled
                          ? { width: `${(elapsedSeconds / totalSeconds) * 100}%` }
                          : undefined
                      }
                    />
                  </div>
                  <p className="stitch-preparation-note">
                    {scheduled
                      ? "Sayaç anketinizin gönderildiği anda başladı."
                      : "Hazırlık ve yayın süreci otomatik takip ediliyor."}
                  </p>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {dailyProgramAvailable && (
        <DailyProgramCard
          program={automation.daily_program}
          refresh={refresh}
          onOpenGuide={onOpenGuide}
          onCompleted={onExperienceJournalOpen}
        />
      )}

      {tablePlanAvailable && !dailyProgramAvailable && (
        <div className="p-5 sm:p-7">
          <div className="grid gap-3 md:hidden">
            {automation.plan.blocks.map((week) => (
              <article
                key={`${week.week_number}-${week.offset_days}-mobile`}
                className="border border-pleiades-border bg-white p-4"
              >
                <div className="text-[11px] font-semibold text-pleiades-blue">
                  {week.week_number}. hafta
                </div>
                <h3 className="mt-2 text-[16px] font-semibold text-pleiades-charcoal">
                  {week.esma_name}
                </h3>
                <dl className="mt-3 divide-y divide-pleiades-border text-[12px]">
                  <div className="flex min-h-11 items-center justify-between gap-4 py-2">
                    <dt className="text-pleiades-muted">Sesli tekrar</dt>
                    <dd className="text-right font-medium">{week.vocal_minutes} dakika</dd>
                  </div>
                  <div className="flex min-h-11 items-center justify-between gap-4 py-2">
                    <dt className="text-pleiades-muted">Meditasyon</dt>
                    <dd className="text-right font-mono font-semibold">{week.meditation_code}</dd>
                  </div>
                  <div className="flex min-h-11 items-center justify-between gap-4 py-2">
                    <dt className="text-pleiades-muted">Süre ve pozisyon</dt>
                    <dd className="text-right font-medium">
                      {week.meditation_minutes} dakika · {week.meditation_position}
                    </dd>
                  </div>
                </dl>
              </article>
            ))}
          </div>
          <div className="hidden overflow-x-auto border border-pleiades-border md:block">
            <table className="w-full min-w-[760px] border-collapse text-left">
              <caption className="sr-only">Program süresinin haftalık çalışma programı</caption>
              <thead className="bg-pleiades-ivory text-pleiades-charcoal">
                <tr>
                  <th
                    scope="col"
                    className="w-20 px-4 py-3 border-b border-r border-pleiades-border text-[11px] uppercase tracking-[.12em]"
                  >
                    Hafta
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 border-b border-r border-pleiades-border text-[11px] uppercase tracking-[.12em]"
                  >
                    Esmâ çalışması
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 border-b border-r border-pleiades-border text-[11px] uppercase tracking-[.12em]"
                  >
                    Sesli tekrar
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 border-b border-pleiades-border text-[11px] uppercase tracking-[.12em]"
                  >
                    Meditasyon
                  </th>
                </tr>
              </thead>
              <tbody>
                {automation.plan.blocks.map((week) => (
                  <tr
                    key={`${week.week_number}-${week.offset_days}`}
                    className="align-top odd:bg-white even:bg-pleiades-ivory/45"
                  >
                    <th
                      scope="row"
                      className="px-4 py-4 border-b border-r border-pleiades-border text-[13px] font-semibold text-pleiades-blue"
                    >
                      {week.week_number}. hafta
                    </th>
                    <td className="px-4 py-4 border-b border-r border-pleiades-border">
                      <div className="text-[14px] font-semibold text-pleiades-charcoal">
                        {week.esma_name}
                      </div>
                      <div className="mt-1 text-[12px] leading-[1.55] text-pleiades-muted">
                        {week.esma_position} · {week.environment} · gözler{" "}
                        {week.eyes_closed ? "kapalı" : "açık"}
                      </div>
                    </td>
                    <td className="px-4 py-4 border-b border-r border-pleiades-border">
                      <div className="text-[13px] font-medium text-pleiades-charcoal">
                        {week.vocal_minutes} dakika
                      </div>
                      <div className="mt-1 text-[12px] leading-[1.55] text-pleiades-muted">
                        Her {week.repeat_interval_seconds} saniyede bir sesli tekrar
                      </div>
                    </td>
                    <td className="px-4 py-4 border-b border-pleiades-border">
                      <div className="font-mono text-[13px] font-semibold tracking-[.08em] text-pleiades-charcoal">
                        {week.meditation_code}
                      </div>
                      <div className="mt-1 text-[12px] leading-[1.55] text-pleiades-muted">
                        Başlangıçta {week.code_repeat_count} kez · {week.meditation_minutes} dakika
                        · {week.meditation_position}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      </section>
    </>
  );
}

function StatusPill({ action }) {
  const done = action === "follow_plan";
  const Icon = done ? CheckCircle2 : action === "complete_questionnaire" ? ShieldCheck : Clock;
  return (
    <span
      className={`shrink-0 inline-flex items-center gap-2 min-h-9 px-3 border text-[11px] ${done ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-pleiades-border bg-pleiades-ivory text-pleiades-slate"}`}
    >
      <Icon size={14} />
      {done ? "Aktif" : "İşlem sürüyor"}
    </span>
  );
}
